import { OrderByDirection } from '@angular/fire/firestore';
import { SortDirection as MatSortDirection } from '@angular/material/sort';
import type { GenEnum } from '@bufbuild/protobuf/codegenv2';
import {
  enumName,
  enumNumber,
  enumOptions,
  SortDirection,
  SortDirectionJson,
  SortDirectionSchema,
} from '@vmspdb/definitions';

export const defaultPagination = 200;
export const paginationOptions = [10, 25, 50, 100, 200, 250];

export function getFirestoreSortDirection(
  dir: SortDirection | SortDirectionJson,
): OrderByDirection | undefined {
  switch (enumNumber(SortDirectionSchema, dir)) {
    case SortDirection.ASC:
      return 'asc';
    case SortDirection.DESC:
      return 'desc';
  }

  return undefined;
}

/**
 * Maps a Material sort direction onto the proto name the API expects.
 *
 * Returns the JSON form rather than the numeric enum because the value is
 * assigned straight onto a `*ListRequestJson` and handed to `fromJson`.
 */
export function getProtoSortDirection(
  dir: MatSortDirection,
): SortDirectionJson {
  switch (dir) {
    case 'asc':
      return enumName(SortDirectionSchema, SortDirection.ASC);
    case 'desc':
      return enumName(SortDirectionSchema, SortDirection.DESC);
  }

  return enumName(SortDirectionSchema, SortDirection.UNSPECIFIED);
}

/**
 * Options for a select bound to an enum field.
 *
 * `value` is the proto name, so a selected option can be written straight onto
 * a `*Json` model without translation. Anything comparing against these values
 * should use `enumEquals`, which accepts every representation.
 */
export function getEnumOptions<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  includeUnspecified = false,
  unSpecifiedLabel = 'Custom',
): { value: J; label: string }[] {
  return enumOptions(schema, {
    includeUnspecified,
    unspecifiedLabel: unSpecifiedLabel,
  }).map((option) => ({
    value: option.name,
    label: option.label,
  }));
}
